import { Component, inject, model, output } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Dialog } from 'primeng/dialog';
import {
  LoginUsersClient,
  SetPasswordCommand,
} from '../../../../infrastructure/shared/model/fss.api';
import {
  ButtonComponent,
  ButtonSeverity,
  ButtonSize,
} from '../../../../infrastructure/shared/ui/button/button';
import { FieldComponent } from '../../../../infrastructure/shared/ui/field/field';
import { TextInput } from '../../../../infrastructure/shared/ui/text-input/text-input';
import { Lock03 } from '../../../../infrastructure/shared/icons/lock-03/lock-03';
import { XClose } from '../../../../infrastructure/shared/icons/x-close/x-close';
import { AlertService } from '../../../../infrastructure/shared/services/alert.service';
import { LoaderService } from '../../../../infrastructure/shared/components/loader/loader.service';

import { PasswordRequirements } from '../../../../infrastructure/shared/components/password-requirements/password-requirements';

@Component({
  standalone: true,
  selector: 'app-change-password-dialog',
  imports: [
    PasswordRequirements,
    Dialog,
    ButtonComponent,
    FieldComponent,
    TextInput,
    Lock03,
    XClose,
  ],
  templateUrl: './change-password-dialog.html',
  styleUrl: './change-password-dialog.scss',
})
export class ChangePasswordDialog {
  private readonly _loginUsers = inject(LoginUsersClient);
  private readonly _alert = inject(AlertService);
  private readonly _loader = inject(LoaderService);

  isVisible = model.required<boolean>();
  passwordChanged = output<void>();

  protected readonly ButtonSeverity = ButtonSeverity;
  protected readonly ButtonSize = ButtonSize;

  protected oldPasswordCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });
  protected newPasswordCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required, Validators.minLength(6)],
  });
  protected confirmPasswordCtrl = new FormControl<string>('', {
    nonNullable: true,
    validators: [Validators.required],
  });

  protected onCancel(): void {
    this._reset();
    this.isVisible.set(false);
  }

  protected async onSubmit(): Promise<void> {
    this.oldPasswordCtrl.markAsTouched();
    this.newPasswordCtrl.markAsTouched();
    this.confirmPasswordCtrl.markAsTouched();

    if (
      this.oldPasswordCtrl.invalid ||
      this.newPasswordCtrl.invalid ||
      this.confirmPasswordCtrl.invalid
    ) {
      return;
    }

    if (this.newPasswordCtrl.value !== this.confirmPasswordCtrl.value) {
      this._alert.addFailedMsg('Lozinke se ne poklapaju.');
      return;
    }

    const cmd = new SetPasswordCommand({
      oldPassword: this.oldPasswordCtrl.value,
      newPassword: this.newPasswordCtrl.value,
      confirmPassword: this.confirmPasswordCtrl.value,
    });

    this._loader.setLoadingText = 'Promena lozinke...';
    this._loader.setShowLoader = true;
    try {
      const res = await firstValueFrom(this._loginUsers.setPassword(cmd));
      this._alert.addSuccessMsg(res.result ?? 'Lozinka je promenjena.');
      this.passwordChanged.emit();
      this._reset();
      this.isVisible.set(false);
    } finally {
      this._loader.setShowLoader = false;
    }
  }

  private _reset(): void {
    this.oldPasswordCtrl.reset();
    this.newPasswordCtrl.reset();
    this.confirmPasswordCtrl.reset();
  }
}
